import { forwardRef, type ComponentProps, type ReactNode } from 'react';
import { Button as HeroButton, buttonVariants } from '@heroui/react/button';
import { Loader2 } from 'lucide-react';
import { cn } from '@/utils/cn';

/**
 * The app's only button.
 *
 * Four variants, three sizes — see DESIGN_SYSTEM.md. Status colours
 * (success/warning/info) are deliberately absent: those communicate state,
 * which is Badge's job, not an action's.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANT = {
  primary: 'primary',
  secondary: 'outline',
  ghost: 'ghost',
  danger: 'danger',
} as const satisfies Record<ButtonVariant, string>;

type HeroButtonProps = ComponentProps<typeof HeroButton>;

export interface ButtonProps extends Omit<
  HeroButtonProps,
  'variant' | 'size' | 'children'
> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /**
   * HTML-style alias for `isDisabled`. HeroUI is react-aria based and expects
   * `isDisabled`; this keeps plain `disabled` working so form code and native
   * habits don't break.
   */
  disabled?: boolean;
  /** Native tooltip text. Not part of HeroUI's prop set, so declared here. */
  title?: string;
  /** Shows a spinner and blocks interaction. */
  isLoading?: boolean;
  /**
   * Label shown while `isLoading`. Prefer setting it — "Saving…" tells the
   * user what is happening, where a bare spinner does not.
   */
  loadingText?: ReactNode;
  /** Icon before the label. Sized by the button automatically. */
  startIcon?: ReactNode;
  /** Icon after the label. */
  endIcon?: ReactNode;
  /** Stretches to the container — use for mobile CTAs. */
  fullWidth?: boolean;
  children?: ReactNode;
  type?: 'button' | 'submit' | 'reset';
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      loadingText,
      isDisabled,
      disabled,
      startIcon,
      endIcon,
      fullWidth,
      className,
      children,
      ...rest
    },
    ref,
  ) => (
    <HeroButton
      ref={ref}
      variant={VARIANT[variant]}
      size={size}
      fullWidth={fullWidth}
      isDisabled={(isDisabled ?? disabled ?? false) || isLoading}
      className={cn('ds-button', className)}
      {...rest}
    >
      {isLoading ? (
        <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
      ) : (
        startIcon
      )}
      {isLoading && loadingText ? loadingText : children}
      {!isLoading && endIcon}
    </HeroButton>
  ),
);
Button.displayName = 'Button';

export interface IconButtonProps extends Omit<
  ButtonProps,
  'startIcon' | 'endIcon' | 'fullWidth'
> {
  /** Required — icon-only buttons must still be announced to screen readers. */
  'aria-label': string;
}

/** Icon-only button. Keeps a 44px tap target regardless of visual size. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      variant = 'ghost',
      size = 'md',
      className,
      children,
      isLoading,
      isDisabled,
      disabled,
      ...rest
    },
    ref,
  ) => (
    <HeroButton
      ref={ref}
      isIconOnly
      variant={VARIANT[variant]}
      size={size}
      isDisabled={(isDisabled ?? disabled ?? false) || isLoading}
      className={cn('ds-button ds-icon-button', className)}
      {...rest}
    >
      {isLoading ? (
        <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
      ) : (
        children
      )}
    </HeroButton>
  ),
);
IconButton.displayName = 'IconButton';

export interface LinkButtonProps extends Omit<
  ComponentProps<'a'>,
  'className'
> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  fullWidth?: boolean;
  className?: string;
  children?: ReactNode;
}

/**
 * An anchor that looks like a Button.
 *
 * HeroUI's Button is a react-aria `<button>` primitive and cannot render as an
 * `<a>`, but `tel:`, `mailto:` and `wa.me` targets must be real links so the
 * OS handles them and middle-click / long-press work. Sharing `ds-button` and
 * the same variant classes keeps it visually identical to Button.
 */
export const LinkButton = forwardRef<HTMLAnchorElement, LinkButtonProps>(
  (
    {
      variant = 'secondary',
      size = 'sm',
      startIcon,
      endIcon,
      fullWidth,
      className,
      children,
      ...rest
    },
    ref,
  ) => (
    <a
      ref={ref}
      className={cn(
        // The same class list HeroUI's own Button emits, so the anchor is
        // pixel-identical rather than a hand-painted lookalike.
        buttonVariants({ variant: VARIANT[variant], size, fullWidth }),
        'ds-button no-underline',
        className,
      )}
      {...rest}
    >
      {startIcon}
      {children}
      {endIcon}
    </a>
  ),
);
LinkButton.displayName = 'LinkButton';
